import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Tooltip,
  Chip,
  TableSortLabel,
  Button,
  Pagination,
  Typography,
  InputAdornment,
  IconButton,
  TextField,
  Checkbox,
} from "@mui/material";
import Select from "react-select";
import {
  RemoveRedEye,
  Edit,
  Search,
  Clear,
  Delete,
  LocalOffer,
  Percent,
} from "@mui/icons-material";
import { Loader, PopUpWindow, OfferForm } from "../../components";
import axios from "axios";
import apiUrl from "../api";
import io from "socket.io-client"; // Add this import
import "./offerTable.css";

// Function to generate a color based on the offer name
const getColor = (name) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const color = `hsl(${hash % 360}, 70%, 60%)`;
  return color;
};

function formatDate(date) {
  if (!date) return "N/A";
  date = new Date(date);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

const findContrastColor = (hexColor) => {
  // Convert hex to RGB if it's a hex color
  if (hexColor.startsWith("#")) {
    const r = parseInt(hexColor.substr(1, 2), 16);
    const g = parseInt(hexColor.substr(3, 2), 16);
    const b = parseInt(hexColor.substr(5, 2), 16);
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 128 ? "#000" : "#fff";
  } else if (hexColor.startsWith("hsl")) {
    // For HSL colors, we'll just make a simple approximation
    const lightnessMatch = hexColor.match(/\d+%\)$/);
    if (lightnessMatch) {
      const lightness = parseInt(lightnessMatch[0]);
      return lightness > 50 ? "#000" : "#fff";
    }
    return "#fff";
  }
  return "#fff";
};

const stringOutArray = (array) => {
  let string = "";
  if (array === null || array === undefined || array === "") {
    return string;
  }
  array.forEach((element) => {
    string += element.value._id + ",";
  });
  return string.slice(0, -1);
};

const convertHTMLToText = (html) => {
  if (!html) return "No description";
  let doc = new DOMParser().parseFromString(html, "text/html");
  return (
    doc.body.textContent ||
    doc.body.innerText ||
    doc.body.innerHTML.replace(/<[^>]*>/g, "") ||
    "No description"
  );
};

// Function to check if an offer is currently active
const isOfferActive = (startDate, endDate) => {
  const now = new Date();
  const start = startDate ? new Date(startDate) : null;
  const end = endDate ? new Date(endDate) : null;

  // If no dates are specified, consider always active
  if (!start && !end) return true;

  // If only start date is specified, active if current date is after start
  if (start && !end) return now >= start;

  // If only end date is specified, active if current date is before end
  if (!start && end) return now <= end;

  // If both dates are specified, active if current date is between them
  return now >= start && now <= end;
};

const OfferTable = (
  props = {
    mini: false,
    visibleSearch: true,
  }
) => {
  const [deleteOfferOpen, setDeleteOfferOpen] = useState(false);
  const [offerIsOpen, setOfferIsOpen] = useState(false);
  const [editOfferOpen, setEditOfferOpen] = useState(false);
  const [openOffer, setOpenOffer] = useState();
  const [offers, setOffers] = useState([]);
  const [totalOffers, setTotalOffers] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [offersPerPage, setOffersPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("created_at");
  const [sortOrder, setSortOrder] = useState(-1); // -1 for descending, 1 for ascending
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [searchProperty, setSearchProperty] = useState("");
  const [selectedOffers, setSelectedOffers] = useState([]);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [socket, setSocket] = useState(null);

  const user = JSON.parse(localStorage.getItem("user"));

  // Initialize socket connection when component mounts
  useEffect(() => {
    const newSocket = io(apiUrl, { transports: ["websocket"] });
    setSocket(newSocket);

    // Clean up socket connection when component unmounts
    return () => {
      newSocket.disconnect();
    };
  }, []);

  // Socket event listeners
  useEffect(() => {
    if (!socket) return;

    // Listen for offer creation event
    socket.on("offerCreated", (data) => {
      setOffers((prevOffers) => {
        // Only add if it's not already in the list
        if (!prevOffers.some((offer) => offer._id === data.offer._id)) {
          return [data.offer, ...prevOffers];
        }
        return prevOffers;
      });
      setTotalOffers((prev) => prev + 1);
    });

    // Listen for offer update event
    socket.on("offerUpdated", (data) => {
      setOffers((prevOffers) => {
        return prevOffers.map((offer) =>
          offer._id === data.offer._id ? data.offer : offer
        );
      });
    });

    // Listen for offer deletion event
    socket.on("offerDeleted", (data) => {
      setOffers((prevOffers) =>
        prevOffers.filter((offer) => offer._id !== data.offerId)
      );
      setTotalOffers((prev) => prev - 1);
    });

    // Clean up event listeners when component unmounts
    return () => {
      socket.off("offerCreated");
      socket.off("offerUpdated");
      socket.off("offerDeleted");
    };
  }, [socket]);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(
        `${apiUrl}/property/getPropertiesNames`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      setProperties(response.data.propertiesNames);
    } catch (error) {
      console.error("Failed to fetch properties: ", error);
    }
  };

  const fetchOffers = async (
    term = "",
    startDate = "",
    endDate = "",
    property = ""
  ) => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${apiUrl}/offer/paginated?search=${term}&startDate=${startDate}&endDate=${endDate}&property=${stringOutArray(
          property
        )}&sortField=${sortField}&sortOrder=${
          sortOrder === 1 ? "asc" : "desc"
        }&page=${currentPage}&limit=${offersPerPage}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      setOffers(response.data.offers || []);
      setTotalOffers(response.data.totalItems || 0);
      setPageCount(Math.ceil((response.data.totalItems || 0) / offersPerPage));
    } catch (err) {
      console.error(err);
      setError("Failed to fetch offers");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenOffer = (offer) => {
    setOpenOffer(offer);
    setOfferIsOpen(true);
  };

  const handleEditOffer = (offer) => {
    setOpenOffer(offer);
    setEditOfferOpen(true);
  };

  const handleDeleteOffer = async (offerId) => {
    try {
      await axios.delete(`${apiUrl}/offer/${offerId}`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      });
      // No need to manually update state here as the socket will handle it
      // But keep the refresh trigger for cases where socket might fail
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Failed to delete offer: ", error);
      setError("Failed to delete offer");
    }
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    fetchOffers(searchTerm, startDate, endDate, searchProperty);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleClearSearch = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setSearchTerm("");
    setStartDate("");
    setEndDate("");
    setSortField("created_at");
    setSortOrder(-1);
    setCurrentPage(1);
    setSearchProperty("");
    fetchOffers();
  };

  const handleSortChange = (field) => {
    if (sortField === field) {
      // Toggle sort order if the same field was clicked
      setSortOrder(sortOrder * -1);
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortOrder(1);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedOffers(offers.map((offer) => offer._id));
    } else {
      setSelectedOffers([]);
    }
  };

  const handleSelectOne = (event, id) => {
    if (event.target.checked) {
      setSelectedOffers((prevSelected) => [...prevSelected, id]);
    } else {
      setSelectedOffers((prevSelected) =>
        prevSelected.filter((offerId) => offerId !== id)
      );
    }
  };

  const handleBulkDelete = async () => {
    try {
      for (const offerId of selectedOffers) {
        await handleDeleteOffer(offerId);
      }
      setSelectedOffers([]);
      setRefreshTrigger((prev) => prev + 1);
    } catch (err) {
      console.error(err);
      setError("Failed to delete offers");
    }
  };

  useEffect(() => {
    fetchOffers(searchTerm, startDate, endDate, searchProperty);
  }, [currentPage, offersPerPage, sortField, sortOrder, refreshTrigger]);

  useEffect(() => {
    fetchProperties();
    fetchOffers();
  }, []);

  const handleOfferUpdated = () => {
    // Still keep the refresh trigger as a fallback
    setRefreshTrigger((prev) => prev + 1);
    setEditOfferOpen(false);
    setOfferIsOpen(false);
  };

  return (
    <>
      {isLoading && <Loader />}

      {offerIsOpen && (
        <PopUpWindow
          onClose={() => setOfferIsOpen(false)}
          color={"#484C4F"}
          title="Offer Details"
          width={"65%"}
        >
          <Box sx={{ p: 3 }}>
            <Typography variant="h5">{openOffer.name}</Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Description
              </Typography>
              <Box
                sx={{
                  mt: 1,
                  mb: 2,
                  p: 2,
                  backgroundColor: "#f5f5f5",
                  borderRadius: "4px",
                }}
              >
                <div
                  dangerouslySetInnerHTML={{ __html: openOffer.description }}
                ></div>
              </Box>
            </Box>
            <Box sx={{ display: "flex", mt: 2, gap: 4 }}>
              <Box>
                <Typography variant="subtitle1">Discount</Typography>
                <Chip
                  icon={<Percent />}
                  label={`${openOffer.discount || 0}%`}
                  color="primary"
                  sx={{ mt: 1, fontSize: "1rem" }}
                />
              </Box>
              <Box>
                <Typography variant="subtitle1">Valid Period</Typography>
                <Typography sx={{ mt: 1 }}>
                  {formatDate(openOffer.start_date)} -{" "}
                  {formatDate(openOffer.end_date)}
                </Typography>
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle1">Properties</Typography>
                <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {openOffer.properties &&
                    openOffer.properties.map((property) => (
                      <Chip
                        key={property._id}
                        label={property.name}
                        size="small"
                      />
                    ))}
                  {(!openOffer.properties ||
                    openOffer.properties.length === 0) && (
                    <Typography variant="body2">
                      No properties assigned
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 4,
                gap: 2,
              }}
            >
              {user.permissions.canEditOffer && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setOfferIsOpen(false);
                    handleEditOffer(openOffer);
                  }}
                >
                  Edit Offer
                </Button>
              )}
              {user.permissions.canDeleteOffer && (
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => {
                    setOfferIsOpen(false);
                    setOpenOffer(openOffer);
                    setDeleteOfferOpen(true);
                  }}
                >
                  Delete Offer
                </Button>
              )}
            </Box>
          </Box>
        </PopUpWindow>
      )}

      {editOfferOpen && (
        <PopUpWindow
          onClose={() => setEditOfferOpen(false)}
          title="Edit Offer"
          width={"65%"}
        >
          <OfferForm
            onClose={() => setEditOfferOpen(false)}
            onSuccess={handleOfferUpdated}
            editMode={true}
            offerData={openOffer}
          />
        </PopUpWindow>
      )}

      {deleteOfferOpen && (
        <PopUpWindow
          onClose={() => setDeleteOfferOpen(false)}
          title="Delete Offer"
          width={"500px"}
        >
          <Box sx={{ p: 3 }}>
            <Typography variant="h6">
              Are you sure you want to delete{" "}
              {selectedOffers.length > 0
                ? `${selectedOffers.length} offers`
                : "this offer"}
              ?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              This action cannot be undone.
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 4,
                gap: 2,
              }}
            >
              <Button
                variant="outlined"
                onClick={() => setDeleteOfferOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => {
                  if (selectedOffers.length > 0) {
                    handleBulkDelete();
                  } else if (openOffer) {
                    handleDeleteOffer(openOffer._id);
                  }
                  setDeleteOfferOpen(false);
                }}
              >
                Delete
              </Button>
            </Box>
          </Box>
        </PopUpWindow>
      )}

      {!isLoading && (
        <>
          {!props.mini && props.visibleSearch && (
            <form
              onSubmit={handleSearchSubmit}
              style={{
                display: "flex",
                flexDirection: "column",
                width: "95%",
                padding: "10px",
              }}
            >
              <div style={{ display: "flex", flexDirection: "row" }}>
                <TextField
                  variant="outlined"
                  placeholder="Search offers"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <Tooltip title="Clear search">
                          <IconButton
                            onClick={() => {
                              setSearchTerm("");
                              fetchOffers("");
                            }}
                            aria-label="clear search"
                          >
                            <Clear />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  label="Start Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  sx={{ marginLeft: "10px", width: "20%" }}
                />
                <TextField
                  label="End Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  sx={{ marginLeft: "10px", width: "20%" }}
                />
              </div>

              <div
                className="form-group"
                style={{
                  zIndex: 1001,
                }}
              >
                <div className="form-field">
                  <label htmlFor="property" className="form-field-label">
                    Property:
                  </label>
                  <Select
                    id="property"
                    name="property"
                    value={searchProperty}
                    onChange={(selectedOption) =>
                      setSearchProperty(selectedOption)
                    }
                    theme={(theme) => ({
                      ...theme,
                      colors: {
                        ...theme.colors,
                        primary: "var(--primary-color)",
                        primary25: "var(--secondary-color)",
                      },
                    })}
                    options={
                      properties &&
                      properties.map((property) => ({
                        value: property,
                        label: property.name,
                      }))
                    }
                    isClearable
                    isMulti
                  />
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  marginTop: "10px",
                }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  style={{
                    padding: "10px",
                    width: "20%",
                    backgroundColor: "var(--primary-color)",
                    color: "var(--primary-text-color)",
                    fontSize: "15px",
                    fontWeight: "500",
                  }}
                  onClick={handleSearchSubmit}
                >
                  Search
                </Button>

                <Button
                  variant="contained"
                  color="secondary"
                  style={{
                    marginLeft: "10px",
                    padding: "10px",
                    width: "20%",
                    backgroundColor: "var(--secondary-color)",
                    color: "var(--primary-text-color)",
                    fontSize: "15px",
                    fontWeight: "500",
                  }}
                  onClick={handleClearSearch}
                >
                  Clear
                </Button>
              </div>
            </form>
          )}

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            m={2}
          >
            {selectedOffers.length > 0 && (
              <Box display="flex" gap={2}>
                {user.permissions.canDeleteOffer && (
                  <Button
                    variant="contained"
                    color="error"
                    onClick={() => {
                      setDeleteOfferOpen(true);
                    }}
                  >
                    Delete Selected
                  </Button>
                )}
              </Box>
            )}
          </Box>

          <TableContainer
            component={Paper}
            sx={{
              maxheight: 540,
              overflow: "auto",
              overflowX: "scroll",
              width: "100%",
              paddingRight: "20px",
            }}
          >
            <Table
              aria-label="offers table"
              stickyHeader
              sx={{
                padding: "10px",
              }}
            >
              <TableHead>
                <TableRow>
                  {user.permissions.canDeleteOffer && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={
                          selectedOffers.length > 0 &&
                          selectedOffers.length < offers.length
                        }
                        checked={
                          offers.length > 0 &&
                          selectedOffers.length === offers.length
                        }
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                  )}
                  <TableCell></TableCell>

                  <TableCell align="center">
                    <TableSortLabel
                      active={sortField === "name"}
                      direction={
                        sortField === "name" && sortOrder === 1 ? "asc" : "desc"
                      }
                      onClick={() => handleSortChange("name")}
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>

                  {/* Add Status column header */}
                  <TableCell align="center">Status</TableCell>

                  <TableCell align="center">
                    <TableSortLabel
                      active={sortField === "description"}
                      direction={
                        sortField === "description" && sortOrder === 1
                          ? "asc"
                          : "desc"
                      }
                      onClick={() => handleSortChange("description")}
                    >
                      Description
                    </TableSortLabel>
                  </TableCell>

                  <TableCell align="center">
                    <TableSortLabel
                      active={sortField === "discount"}
                      direction={
                        sortField === "discount" && sortOrder === 1
                          ? "asc"
                          : "desc"
                      }
                      onClick={() => handleSortChange("discount")}
                    >
                      Discount
                    </TableSortLabel>
                  </TableCell>

                  <TableCell align="center">
                    <TableSortLabel
                      active={sortField === "start_date"}
                      direction={
                        sortField === "start_date" && sortOrder === 1
                          ? "asc"
                          : "desc"
                      }
                      onClick={() => handleSortChange("start_date")}
                    >
                      Start Date
                    </TableSortLabel>
                  </TableCell>

                  <TableCell align="center">
                    <TableSortLabel
                      active={sortField === "end_date"}
                      direction={
                        sortField === "end_date" && sortOrder === 1
                          ? "asc"
                          : "desc"
                      }
                      onClick={() => handleSortChange("end_date")}
                    >
                      End Date
                    </TableSortLabel>
                  </TableCell>

                  <TableCell align="center">Properties</TableCell>

                  <TableCell align="center">
                    <TableSortLabel
                      active={sortField === "created_at"}
                      direction={
                        sortField === "created_at" && sortOrder === 1
                          ? "asc"
                          : "desc"
                      }
                      onClick={() => handleSortChange("created_at")}
                    >
                      Created
                    </TableSortLabel>
                  </TableCell>

                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {offers.map((offer, index) => (
                  <TableRow
                    key={index}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    maxheight="100px"
                  >
                    {user.permissions.canDeleteOffer && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedOffers.includes(offer._id)}
                          onChange={(event) =>
                            handleSelectOne(event, offer._id)
                          }
                        />
                      </TableCell>
                    )}
                    <TableCell sx={{ padding: "0px" }}>
                      <Tooltip title="View" placement="right">
                        <Button
                          onClick={() => {
                            handleOpenOffer(offer);
                          }}
                        >
                          <RemoveRedEye
                            sx={{ color: "var(--primary-color)" }}
                          />
                        </Button>
                      </Tooltip>

                      {user.permissions.canEditOffer && (
                        <Tooltip title="Edit" placement="right">
                          <Button
                            onClick={() => {
                              handleEditOffer(offer);
                            }}
                          >
                            <Edit sx={{ color: "var(--primary-color)" }} />
                          </Button>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="left">
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {/* <LocalOffer sx={{ color: getColor(offer.name), fontSize: '1.2rem' }} /> */}
                        {offer.name}
                      </Box>
                    </TableCell>

                    {/* Add Status cell with active/inactive chip */}
                    <TableCell align="center">
                      <Chip
                        label={
                          isOfferActive(offer.start_date, offer.end_date)
                            ? "Active"
                            : "Inactive"
                        }
                        color={
                          isOfferActive(offer.start_date, offer.end_date)
                            ? "success"
                            : "default"
                        }
                        size="small"
                        sx={{ fontWeight: "medium" }}
                      />
                    </TableCell>

                    <TableCell
                      align="left"
                      style={{
                        maxWidth: "150px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Tooltip
                        title={convertHTMLToText(offer.description)}
                        PopperProps={{
                          style: { zIndex: 10001 },
                          modifiers: [
                            {
                              name: "offset",
                              options: {
                                offset: [0, -10],
                              },
                            },
                          ],
                          sx: {
                            "& .MuiTooltip-tooltip": {
                              fontSize: "13px",
                            },
                          },
                        }}
                      >
                        <span>
                          {(
                            convertHTMLToText(offer.description) || ""
                          ).substring(0, 50) +
                            ((convertHTMLToText(offer.description) || "")
                              .length > 50
                              ? "..."
                              : "")}
                        </span>
                      </Tooltip>
                    </TableCell>

                    <TableCell align="center">
                      <Chip
                        label={`${offer.discount || 0}%`}
                        color={offer.discount > 0 ? "success" : "default"}
                        variant={offer.discount > 0 ? "filled" : "outlined"}
                      />
                    </TableCell>

                    <TableCell align="center">
                      {formatDate(offer.start_date)}
                    </TableCell>

                    <TableCell align="center">
                      {formatDate(offer.end_date)}
                    </TableCell>

                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.5,
                          justifyContent: "center",
                        }}
                      >
                        {offer.properties && offer.properties.length > 0 ? (
                          offer.properties
                            .slice(0, 2)
                            .map((property) => (
                              <Chip
                                key={property._id}
                                label={property.name}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: "0.75rem" }}
                              />
                            ))
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            None
                          </Typography>
                        )}

                        {offer.properties && offer.properties.length > 2 && (
                          <Tooltip
                            title={
                              <Box>
                                {offer.properties.slice(2).map((property) => (
                                  <Typography
                                    key={property._id}
                                    variant="body2"
                                  >
                                    {property.name}
                                  </Typography>
                                ))}
                              </Box>
                            }
                          >
                            <Chip
                              label={`+${offer.properties.length - 2}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ fontSize: "0.75rem" }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>

                    <TableCell align="center">
                      {formatDate(offer.created_at)}
                    </TableCell>

                    <TableCell align="center">
                      {user.permissions.canDeleteOffer && (

                        <Tooltip title="Delete" placement="right">
                          <IconButton
                            onClick={() => {
                              setDeleteOfferOpen(true);
                              setOpenOffer(offer);
                            }}
                            size="small"
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {!props.mini && (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              m={2}
              sx={{ width: "95%" }}
            >
              <Typography>
                Showing{" "}
                {Math.min((currentPage - 1) * offersPerPage + 1, totalOffers)}-
                {Math.min(currentPage * offersPerPage, totalOffers)} of{" "}
                {totalOffers} items
              </Typography>
              <Select
                theme={(theme) => ({
                  ...theme,
                  colors: {
                    ...theme.colors,
                    primary: "var(--primary-color)",
                    primary25: "var(--secondary-color)",
                  },
                })}
                value={{
                  value: offersPerPage,
                  label: offersPerPage,
                }}
                onChange={(option) => {
                  setOffersPerPage(option.value);
                  setCurrentPage(1);
                }}
                displayEmpty
                inputProps={{ "aria-label": "Offers per page" }}
                options={[10, 25, 50, 100].map((option) => ({
                  value: option,
                  label: option,
                }))}
              />
              <Pagination
                count={pageCount}
                page={currentPage}
                onChange={(event, page) => {
                  setCurrentPage(page);
                }}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </>
  );
};

export default OfferTable;
